// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import {LongPressable} from '@app/features/app/components/LongPressable';
import {useContextMenuHoverState} from '@app/features/app/hooks/useContextMenuHoverState';
import listStyles from '@app/features/channel/components/direct_message/DirectMessageList.module.css';
import type {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import * as ChannelUtils from '@app/features/channel/utils/ChannelUtils';
import {type DaoICommunity, useDaoI} from '@app/features/daoi/state/DaoIData';
import {GuildIcon} from '@app/features/guild/components/popouts/GuildIcon';
import type {Guild} from '@app/features/guild/models/Guild';
import {getChannelContribution} from '@app/features/guild/state/GuildReadState';
import Guilds from '@app/features/guild/state/Guilds';
import Messages from '@app/features/messaging/state/MessagingMessages';
import {getChannelSortSnowflake} from '@app/features/messaging/utils/DmChannelUtils';
import * as NavigationCommands from '@app/features/navigation/commands/NavigationCommands';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import Permission from '@app/features/permissions/state/Permission';
import {http} from '@app/features/platform/transport/RestTransport';
import ReadStates from '@app/features/read_state/state/ReadStates';
import ChannelThreads from '@app/features/threads/state/ChannelThreads';
import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import {Button} from '@app/features/ui/button/Button';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import * as LayoutCommands from '@app/features/ui/commands/LayoutCommands';
import {MentionBadge} from '@app/features/ui/components/MentionBadge';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {MenuBottomSheet} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import UserGuildSettings from '@app/features/user/state/UserGuildSettings';
import UserPinnedDM from '@app/features/user/state/UserPinnedDM';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import {useVoiceGatewayStateVersion} from '@app/features/voice/engine/v2/VoiceEngineV2AppVoiceStateAdapter';
import {ChannelTypes, GUILD_TEXT_BASED_CHANNEL_TYPES, Permissions} from '@fluxer/constants/src/ChannelConstants';
import {GuildFeatures} from '@fluxer/constants/src/GuildConstants';
import {THREAD_ONLY_CHANNEL_TYPES} from '@fluxer/constants/src/ThreadConstants';
import {compare, extractTimestamp} from '@fluxer/snowflake/src/SnowflakeUtils';
import {
	BellSlashIcon,
	CaretRightIcon,
	DotsThreeIcon,
	MegaphoneIcon,
	PhoneIcon,
	PushPinIcon,
	SealCheckIcon,
} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {type KeyboardEvent, type MouseEvent, useEffect, useRef, useState} from 'react';

export interface Conversation {
	id: string;
	kind: 'dm' | 'community';
	name: string;
	channel: Channel;
	guild?: Guild;
	unread: number;
	hasUnread: boolean;
	muted: boolean;
	pinned: boolean;
	lastMessageId: string | null;
}

function visibleCommunityChannels(guildId: string): Array<Channel> {
	return Channels.getGuildChannels(guildId).filter(
		(channel) =>
			(GUILD_TEXT_BASED_CHANNEL_TYPES.has(channel.type) || THREAD_ONLY_CHANNEL_TYPES.has(channel.type)) &&
			Permission.can(Permissions.VIEW_CHANNEL, {channelId: channel.id, guildId}),
	);
}

function visibleCommunityActivityChannels(
	guildId: string,
	channels = visibleCommunityChannels(guildId),
): Array<Channel> {
	const parentIds = new Set(channels.map((channel) => channel.id));
	// The native store also checks private-thread membership/management rights.
	const threads = ChannelThreads.getGuildThreads(guildId).filter(
		(thread) =>
			parentIds.has(thread.parentId ?? '') && Permission.can(Permissions.VIEW_CHANNEL, {channelId: thread.id, guildId}),
	);
	return [...channels, ...threads];
}

function lastActivity(channels: ReadonlyArray<Channel>): string | null {
	return channels.reduce<string | null>(
		(latest, channel) =>
			!channel.lastMessageId
				? latest
				: !latest || BigInt(channel.lastMessageId) > BigInt(latest)
					? channel.lastMessageId
					: latest,
		null,
	);
}

function formatTime(messageId: string | null): string {
	if (!messageId) return '';
	const date = new Date(extractTimestamp(messageId));
	return new Intl.DateTimeFormat(
		'zh-CN',
		Date.now() - date.getTime() < 86400000 ? {hour: '2-digit', minute: '2-digit'} : {month: 'numeric', day: 'numeric'},
	).format(date);
}

function preview(conversation: Conversation): string {
	const channels = conversation.guild
		? visibleCommunityActivityChannels(conversation.guild.id)
		: [conversation.channel];
	const active =
		channels.find((channel) => channel.lastMessageId === conversation.lastMessageId) ?? conversation.channel;
	const message = Messages.getCachedMessages(active.id)?.last();
	if (message?.content) return message.content.replace(/\s+/g, ' ').slice(0, 120);
	if (message?.attachments?.length) return '[附件]';
	return conversation.hasUnread ? '有新消息' : conversation.guild ? '进入社区讨论' : '点击开始聊天';
}

export function getDaoIConversations(
	dmChannels: ReadonlyArray<Channel>,
	communities: ReadonlyArray<DaoICommunity>,
	pinnedGuildIds: ReadonlyArray<string>,
): Array<Conversation> {
	const conversations: Array<Conversation> = dmChannels
		.filter((channel) => channel.type !== ChannelTypes.DM_PERSONAL_NOTES)
		.map((channel) => ({
			id: channel.id,
			kind: 'dm',
			channel,
			name: ChannelUtils.getDMDisplayName(channel),
			unread: ReadStates.getUnreadCount(channel.id),
			hasUnread: ReadStates.hasUnread(channel.id),
			muted: UserGuildSettings.isChannelDirectlyMuted(null, channel.id),
			pinned: UserPinnedDM.pinnedDMs.includes(channel.id),
			lastMessageId: channel.lastMessageId,
		}));
	for (const guild of Guilds.getGuilds()) {
		const mapping = communities.find((community) => community.guild_id === guild.id);
		const entryChannels = visibleCommunityChannels(guild.id);
		const channel = entryChannels.find((entry) => entry.id === mapping?.discussion_id) ?? entryChannels[0];
		if (!channel) continue;
		const channels = visibleCommunityActivityChannels(guild.id, entryChannels);
		let unread = 0;
		let hasUnread = false;
		for (const entry of channels) {
			const contribution = getChannelContribution(entry, entry.id);
			hasUnread ||= contribution.unreadAllowed || contribution.mentionAllowed;
			// Native mute/badge rules suppress ordinary messages, but retain mentions.
			unread += contribution.unreadAllowed
				? Math.max(ReadStates.getUnreadCount(entry.id), contribution.mentionCount)
				: contribution.mentionAllowed
					? contribution.mentionCount
					: 0;
		}
		conversations.push({
			id: guild.id,
			kind: 'community',
			channel,
			guild,
			name: guild.name,
			unread,
			hasUnread,
			muted: UserGuildSettings.isWholeGuildMuted(guild.id),
			pinned: pinnedGuildIds.includes(guild.id),
			lastMessageId: lastActivity(channels),
		});
	}
	const pinnedDmOrder = new Map(UserPinnedDM.pinnedDMs.map((id, index) => [id, index]));
	const pinnedGuildOrder = new Map(pinnedGuildIds.map((id, index) => [id, pinnedDmOrder.size + index]));
	return conversations.sort((left, right) => {
		if (left.pinned !== right.pinned) return left.pinned ? -1 : 1;
		if (left.pinned && right.pinned) {
			const leftOrder = (left.kind === 'dm' ? pinnedDmOrder : pinnedGuildOrder).get(left.id) ?? 0;
			const rightOrder = (right.kind === 'dm' ? pinnedDmOrder : pinnedGuildOrder).get(right.id) ?? 0;
			if (leftOrder !== rightOrder) return leftOrder - rightOrder;
		}
		const leftActivity = left.kind === 'dm' ? getChannelSortSnowflake(left.channel) : (left.lastMessageId ?? left.id);
		const rightActivity =
			right.kind === 'dm' ? getChannelSortSnowflake(right.channel) : (right.lastMessageId ?? right.id);
		return compare(rightActivity, leftActivity) || left.id.localeCompare(right.id);
	});
}

export const CommunityConversation = observer(
	({
		entry,
		selected,
		onTogglePin,
		onToggleMute,
	}: {
		entry: Conversation;
		selected: boolean;
		onTogglePin: () => void;
		onToggleMute: () => void;
	}) => {
		const rowRef = useRef<HTMLElement | null>(null);
		const contextMenuOpen = useContextMenuHoverState(rowRef);
		const [menuOpen, setMenuOpen] = useState(false);
		const mobile = MobileLayout.enabled;
		const guild = entry.guild!;
		const navigateToChannel = (channelId: string) => {
			NavigationCommands.selectChannel(guild.id, channelId);
			if (mobile) LayoutCommands.updateMobileLayoutState(false, true);
		};
		const navigate = () => navigateToChannel(entry.channel.id);
		const channelActions = visibleCommunityChannels(guild.id).map((channel) => ({
			id: channel.id,
			label: channel.name ?? '频道',
			onClick: () => navigateToChannel(channel.id),
		}));
		const renderMenu = () => (
			<>
				<MenuGroup data-flx="daoi.community-row.menu-group">
					<MenuItem
						icon={<PushPinIcon size={16} data-flx="daoi.community-row.pin-icon" />}
						onClick={onTogglePin}
						data-flx="daoi.community-row.pin"
					>
						{entry.pinned ? '取消置顶' : '置顶会话'}
					</MenuItem>
					<MenuItem
						icon={<BellSlashIcon size={16} data-flx="daoi.community-row.mute-icon" />}
						onClick={onToggleMute}
						data-flx="daoi.community-row.mute"
					>
						{entry.muted ? '开启通知' : '消息免打扰'}
					</MenuItem>
				</MenuGroup>
				<MenuGroup data-flx="daoi.community-row.channels-group">
					{channelActions.map((action) => (
						<MenuItem key={action.id} onClick={action.onClick} data-flx="daoi.community-row.channel-action">
							{action.label}
						</MenuItem>
					))}
				</MenuGroup>
			</>
		);
		const openOptions = (element: HTMLElement) => {
			if (mobile) setMenuOpen(true);
			else ContextMenuCommands.openForElement(element, renderMenu);
		};
		return (
			<>
				<FocusRing offset={-2} data-flx="daoi.community-row.focus-ring">
					<LongPressable
						ref={rowRef}
						role="link"
						data-dm-list-focus-item="true"
						tabIndex={0}
						aria-label={entry.name}
						aria-current={selected ? 'page' : undefined}
						className={clsx(
							mobile
								? selected
									? listStyles.dmItemMobileSelected
									: entry.hasUnread
										? listStyles.dmItemMobileUnread
										: listStyles.dmItemMobile
								: selected
									? listStyles.dmItemSelected
									: entry.hasUnread
										? listStyles.dmItemUnread
										: listStyles.dmItem,
							entry.muted && (mobile ? listStyles.dmItemMobileMuted : listStyles.dmItemMuted),
							contextMenuOpen && listStyles.contextMenuActive,
						)}
						pressedClassName={listStyles.dmItemMobilePressed}
						onClick={navigate}
						onLongPress={() => {
							if (mobile) setMenuOpen(true);
						}}
						onContextMenu={(event) => {
							event.preventDefault();
							if (mobile) setMenuOpen(true);
							else ContextMenuCommands.openFromEvent(event, renderMenu);
						}}
						onKeyDown={(event) => {
							if (event.target !== event.currentTarget) return;
							if (event.key === 'Enter' || event.key === ' ') {
								event.preventDefault();
								navigate();
							}
							if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) {
								event.preventDefault();
								openOptions(event.currentTarget);
							}
						}}
						data-flx="daoi.community-row.row"
					>
						<div className={listStyles.dmItemContent} data-flx="daoi.community-row.content">
							<div className={listStyles.dmItemAvatarWrapper} data-flx="daoi.community-row.avatar-wrapper">
								<GuildIcon
									id={guild.id}
									name={guild.name}
									icon={guild.icon}
									sizePx={mobile ? 40 : 32}
									data-flx="daoi.community-row.avatar"
								/>
							</div>
							<div className={listStyles.dmItemInfo} data-flx="daoi.community-row.info">
								<span className={listStyles.dmItemName} data-flx="daoi.community-row.name">
									{entry.pinned && (
										<PushPinIcon
											weight="fill"
											className={listStyles.dmItemPinIcon}
											data-flx="daoi.community-row.pinned"
										/>
									)}
									<span className={listStyles.dmItemNameText} data-flx="daoi.community-row.name-text">
										{entry.name}
									</span>
									{guild.features.has(GuildFeatures.VERIFIED) && (
										<SealCheckIcon
											size={16}
											weight="fill"
											className="daoi-accent"
											data-flx="daoi.community-row.verified"
										/>
									)}
								</span>
								<span className={listStyles.dmItemSubtext} data-flx="daoi.community-row.preview">
									{preview(entry)}
								</span>
							</div>
							{entry.hasUnread ? (
								<MentionBadge mentionCount={entry.unread || 1} size="small" data-flx="daoi.community-row.unread" />
							) : entry.muted ? (
								<BellSlashIcon size={14} data-flx="daoi.community-row.muted" />
							) : (
								<time className={listStyles.dmItemTimestamp} data-flx="daoi.community-row.time">
									{formatTime(entry.lastMessageId)}
								</time>
							)}
						</div>
						<Tooltip text="会话选项" data-flx="daoi.community-row.options-tooltip">
							<Button
								variant="ghost"
								square
								superCompact
								className="daoi-community-options"
								icon={<DotsThreeIcon size={18} data-flx="daoi.community-row.options-icon" />}
								aria-label={`${entry.name}的会话选项`}
								aria-haspopup="menu"
								onClick={(event: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => {
									event.stopPropagation();
									openOptions(event.currentTarget);
								}}
								data-flx="daoi.community-row.options"
							/>
						</Tooltip>
					</LongPressable>
				</FocusRing>
				<MenuBottomSheet
					isOpen={menuOpen}
					onClose={() => setMenuOpen(false)}
					title={entry.name}
					groups={[
						{
							items: [
								{label: entry.pinned ? '取消置顶' : '置顶会话', onClick: onTogglePin},
								{label: entry.muted ? '开启通知' : '消息免打扰', onClick: onToggleMute},
							],
						},
						{items: channelActions},
					]}
					data-flx="daoi.community-row.mobile-menu"
				/>
			</>
		);
	},
);

export const DaoICommunityBar = observer(
	({
		guildId,
		onInfo,
		showTitle = true,
		voiceOnly = false,
	}: {
		guildId: string;
		onInfo?: () => void;
		showTitle?: boolean;
		voiceOnly?: boolean;
	}) => {
		useVoiceGatewayStateVersion();
		const {communities, communitiesLoaded} = useDaoI();
		const guild = Guilds.getGuild(guildId);
		const mapping = communities.find((community) => community.guild_id === guildId);
		const announcementChannel =
			!voiceOnly && mapping?.announcement_id ? Channels.getChannel(mapping.announcement_id) : null;
		const announcement = announcementChannel ? Messages.getCachedMessages(announcementChannel.id)?.last() : null;
		const canReadAnnouncement =
			announcementChannel && Permission.can(Permissions.VIEW_CHANNEL, {channelId: announcementChannel.id, guildId});
		const [latestAnnouncement, setLatestAnnouncement] = useState<{channelId: string; content: string} | null>(null);
		const announcementId = canReadAnnouncement ? announcementChannel.id : null;
		const announcementLastId = announcementChannel?.lastMessageId;
		useEffect(() => {
			if (!announcementId) return;
			const controller = new AbortController();
			void http
				.get<Array<{content: string; attachments?: Array<unknown>}>>(`/channels/${announcementId}/messages`, {
					query: {limit: 1},
					signal: controller.signal,
				})
				.then((response) => {
					if (controller.signal.aborted) return;
					const message = response.body[0];
					setLatestAnnouncement({
						channelId: announcementId,
						content: message?.content || (message?.attachments?.length ? '公告包含附件，点击查看' : '查看社区公告'),
					});
				})
				.catch(() => {
					if (!controller.signal.aborted) setLatestAnnouncement(null);
				});
			return () => controller.abort();
		}, [announcementId, announcementLastId]);
		const nativeVoiceChannels =
			mapping || !communitiesLoaded
				? []
				: Channels.getGuildChannels(guildId).filter(
						(channel) =>
							channel.type === ChannelTypes.GUILD_VOICE &&
							Permission.can(Permissions.VIEW_CHANNEL, {guildId, channelId: channel.id}) &&
							Permission.can(Permissions.CONNECT, {guildId, channelId: channel.id}),
					);
		// The native store preserves channel order; an explicit project mapping remains authoritative.
		const voiceChannelId = mapping
			? mapping.voice_id
			: (nativeVoiceChannels.find((channel) => MediaEngine.guildId === guildId && channel.id === MediaEngine.channelId)
					?.id ?? nativeVoiceChannels[0]?.id);
		const voiceParticipants = voiceChannelId
			? Object.keys(MediaEngine.getAllVoiceStatesInChannel(guildId, voiceChannelId)).length
			: 0;
		const canJoinVoice =
			voiceChannelId &&
			Permission.can(Permissions.VIEW_CHANNEL, {guildId, channelId: voiceChannelId}) &&
			Permission.can(Permissions.CONNECT, {guildId, channelId: voiceChannelId});
		if (!guild || (!(showTitle && onInfo) && !canReadAnnouncement && !canJoinVoice)) return null;
		const voiceEntry = voiceChannelId && canJoinVoice && (
			<Button
				type="button"
				variant="secondary"
				compact
				className="daoi-voice-entry"
				onClick={() => {
					if (
						MediaEngine.guildId === guildId &&
						MediaEngine.channelId === voiceChannelId &&
						(MediaEngine.connected || MediaEngine.connecting)
					) {
						NavigationCommands.selectChannel(guildId, voiceChannelId);
					} else {
						void MediaEngine.connectToVoiceChannel(guildId, voiceChannelId);
					}
				}}
				data-flx="daoi.dao-i-conversations.dao-i-community-bar.daoi-voice-entry.button"
			>
				<PhoneIcon size={18} data-flx="daoi.dao-i-conversations.dao-i-community-bar.phone-icon" />
				{MediaEngine.guildId === guildId && MediaEngine.channelId === voiceChannelId
					? '正在通话'
					: voiceParticipants > 0
						? `${voiceParticipants} 人通话中`
						: '群语音'}
			</Button>
		);
		if (voiceOnly) return voiceEntry;
		return (
			<div className="daoi-community-bar" data-flx="daoi.dao-i-conversations.dao-i-community-bar.daoi-community-bar">
				{showTitle && onInfo && (
					<Button
						type="button"
						variant="ghost"
						className="daoi-community-title"
						contentClassName="daoi-row-content"
						onClick={onInfo}
						data-flx="daoi.dao-i-conversations.dao-i-community-bar.daoi-community-title.info.button"
					>
						{guild.name}
						<CaretRightIcon size={18} data-flx="daoi.dao-i-conversations.dao-i-community-bar.caret-right-icon" />
					</Button>
				)}
				{voiceEntry}
				{canReadAnnouncement && (
					<Button
						type="button"
						variant="ghost"
						fitContainer
						className="daoi-announcement"
						contentClassName="daoi-row-content"
						onClick={() => RouterUtils.transitionTo(Routes.guildChannel(guildId, announcementChannel.id))}
						data-flx="daoi.dao-i-conversations.dao-i-community-bar.daoi-announcement.transition-to.button"
					>
						<MegaphoneIcon size={16} data-flx="daoi.dao-i-conversations.dao-i-community-bar.megaphone-icon" />
						<span data-flx="daoi.dao-i-conversations.dao-i-community-bar.span">
							{announcement?.content ||
								(latestAnnouncement?.channelId === announcementChannel.id
									? latestAnnouncement.content
									: '查看社区公告')}
						</span>
						{ReadStates.hasUnread(announcementChannel.id) && (
							<span className="daoi-indicator" data-flx="daoi.dao-i-conversations.dao-i-community-bar.daoi-indicator" />
						)}
						<CaretRightIcon size={16} data-flx="daoi.dao-i-conversations.dao-i-community-bar.caret-right-icon--2" />
					</Button>
				)}
			</div>
		);
	},
);
